import { useCoinBasis } from '../coin'
import {
  genInt,
  genIntOrDouble,
  RuleItem,
  precision,
  genMinOrEqualRule,
  genMinRule,
  genMaxOrEqualRule,
} from '@/utils/validator'
import { useMemo, useEffect } from 'react'
import { isValidNumber } from '@/utils/fee'
import BigNumber from 'bignumber.js'
import {
  FieldRuleMap,
  useFeeValidate,
  FieldKeys,
  CustomizeFeeRateProps,
} from '@/hooks/fee/validator'
import { useTranslation } from '@/services/i18n'

export const useFilFeeRules = (dto: CustomizeFeeRateProps) => {
  const { coinKey, max, mini, value, isSpeed } = dto

  const { t } = useTranslation()

  const coinBasis = useCoinBasis(coinKey)

  const baseRule: RuleItem[] = [genIntOrDouble(), precision(coinBasis?.feeDecimal || 0)]

  // -----------  MaxPriorityFee rules  --------------
  const minValueRuleForMaxPriority = useMemo(
    () =>
      mini?.maxPriorityFee
        ? genMinOrEqualRule(mini.maxPriorityFee!, t('fee.gasPriceLower_4'))
        : null,
    [mini?.maxPriorityFee]
  )
  const maxPriorityforceRules = useMemo<RuleItem[]>(() => {
    const tmp = [...baseRule, genMinRule(0, t('fee.greaterThan0_4'))]
    return tmp
  }, [coinBasis, minValueRuleForMaxPriority])
  const maxPriorityWeakRules = useMemo<RuleItem[]>(() => {
    const rules: RuleItem[] = []
    if (minValueRuleForMaxPriority && !isSpeed) {
      rules.push(minValueRuleForMaxPriority)
    }
    if (max) {
      rules.push(genMaxOrEqualRule(max.maxPriorityFee!, t('fee.gasPriceHigher_4')))
    }
    return rules
  }, [max?.maxPriorityFee, minValueRuleForMaxPriority])

  // ----------- MaxFee rules --------------
  const getMaxFeeForceRules = (baseFee: string, maxPriorityFee?: string) => {
    const tmpRules = [...baseRule]
    if (isValidNumber(maxPriorityFee)) {
      tmpRules.push(genMinOrEqualRule(maxPriorityFee || 0, t('fee.aGreaterThanB_fil')))
    }
    return tmpRules
  }

  const getMaxFeeWeakRules = (baseFee: string, maxPriorityFee?: string) => {
    const rules: RuleItem[] = []
    if (isValidNumber(maxPriorityFee)) {
      rules.push(genMinOrEqualRule(new BigNumber(baseFee).toFixed(), t('fee.lower_3')))
      rules.push(
        genMaxOrEqualRule(
          new BigNumber(baseFee)
            .multipliedBy(Math.pow(1.125, 7))
            .plus(new BigNumber(maxPriorityFee!).multipliedBy(2))
            .toFixed(),
          t('fee.gasPriceHigher_3')
        )
      )
    }
    return rules
  }

  // ----------- gaslimit rules --------------
  const gasLimitForceRule = useMemo(() => {
    const gasLimitRule = [...baseRule, genInt()]
    return gasLimitRule
  }, [mini?.gasLimit])

  const gasLimitWeakRule = useMemo(() => {
    const gasLimitRule = []
    if (mini) {
      gasLimitRule.push(
        genMinOrEqualRule(mini.gasLimit || 0, t('fee.gasLimitGreaterThan', { max: mini.gasLimit }))
      )
    }
    return gasLimitRule
  }, [mini?.gasLimit])

  const fieldRuleMap: FieldRuleMap = {
    maxPriorityFee: {
      force: maxPriorityforceRules,
      weak: maxPriorityWeakRules,
    },
    maxFee: {
      force: getMaxFeeForceRules(mini?.maxFee!, value.maxPriorityFee),
      weak: getMaxFeeWeakRules(mini?.maxFee!, value.maxPriorityFee),
    },
    gasLimit: {
      force: gasLimitForceRule,
      weak: gasLimitWeakRule,
    },
  }

  return fieldRuleMap
}

export const useValidateFilFeeFields = (dto: CustomizeFeeRateProps) => {
  const { onChangeValue, value } = dto

  const [errorMap, doValidate] = useFeeValidate()
  const fieldRuleMap = useFilFeeRules(dto)
  // ----------- validate service --------------
  const validteAndEmitChange = () => {
    const formIsError = doValidate(value, fieldRuleMap)
    onChangeValue?.(formIsError, value)
  }

  const emitChange = <T extends FieldKeys>(fieldKey: T, text: string) => {
    const newValue = { ...value, [fieldKey]: text }
    onChangeValue?.(true, newValue)
  }

  const handleChangeMaxPriorityFee = (text: string) => emitChange('maxPriorityFee', text)
  const handleChangeMaxFee = (text: string) => emitChange('maxFee', text)
  const handleChangeGasLimit = (text: string) => emitChange('gasLimit', text)

  useEffect(() => validteAndEmitChange(), [value])

  return {
    errorMap,
    handleChangeMaxFee,
    handleChangeGasLimit,
    handleChangeMaxPriorityFee,
  }
}
